import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import { reflectionService } from '../../../services/supabaseService';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';

const PRESET_TAGS = [
  'Christ and the Church',
  'Habits to Release',
  'Great Controversy',
  'Nature Lessons',
  'History Lessons',
  'Personal Testimony',
];

const stripStrongs = (text) => {
  if (!text) return '';
  return text?.replace(/\{(?:\()?[HG]\d+(?:\))?\}/g, '')?.trim();
};

const VerseReflectionAccordion = ({ verse, book, chapter, version, onClose, onSaved }) => {
  const { user } = useAuth();

  const [isPrivate, setIsPrivate] = useState(true);
  const [selectedTags, setSelectedTags] = useState([]);
  const [customTagInput, setCustomTagInput] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [meditationText, setMeditationText] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const recognitionRef = useRef(null);
  const textareaRef = useRef(null);
  const customInputRef = useRef(null);

  const hasSpeechSupport =
    typeof window !== 'undefined' && ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window);

  // Init speech recognition
  useEffect(() => {
    if (!hasSpeechSupport) return;
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new SR();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = 'en-US';

    rec.onresult = (event) => {
      let final = '';
      for (let i = event?.resultIndex; i < event?.results?.length; i++) {
        if (event?.results?.[i]?.isFinal) {
          final += event?.results?.[i]?.[0]?.transcript;
        }
      }
      if (final) {
        setMeditationText((prev) => (prev ? prev + ' ' + final?.trim() : final?.trim()));
      }
    };

    rec.onerror = () => setIsRecording(false);
    rec.onend = () => setIsRecording(false);
    recognitionRef.current = rec;

    return () => {
      try { rec?.stop(); } catch (_) {}
    };
  }, [hasSpeechSupport]);

  const toggleTag = (tag) => {
    setSelectedTags((prev) =>
      prev?.includes(tag) ? prev?.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const addCustomTag = () => {
    const trimmed = customTagInput?.trim();
    if (!trimmed) return;
    if (!selectedTags?.includes(trimmed)) {
      setSelectedTags((prev) => [...prev, trimmed]);
    }
    setCustomTagInput('');
    setShowCustomInput(false);
  };

  const handleCustomKeyDown = (e) => {
    if (e?.key === 'Enter') { e?.preventDefault(); addCustomTag(); }
    if (e?.key === 'Escape') { setShowCustomInput(false); setCustomTagInput(''); }
  };

  const toggleDictation = () => {
    if (!recognitionRef?.current) return;
    if (isRecording) {
      recognitionRef?.current?.stop();
      setIsRecording(false);
    } else {
      try {
        recognitionRef?.current?.start();
        setIsRecording(true);
      } catch (_) {
        setIsRecording(false);
      }
    }
  };

  const handleClearAll = () => {
    setSelectedTags([]);
    setCustomTagInput('');
    setShowCustomInput(false);
    setMeditationText('');
    setSaveError(null);
    setSaveSuccess(false);
    if (isRecording) {
      try { recognitionRef?.current?.stop(); } catch (_) {}
      setIsRecording(false);
    }
  };

  const handleCancel = () => {
    if (isRecording) {
      try { recognitionRef?.current?.stop(); } catch (_) {}
      setIsRecording(false);
    }
    onClose?.();
  };

  const handleSave = async () => {
    if (!user?.id) {
      setSaveError('Please sign in to save reflections.');
      return;
    }
    if (!meditationText?.trim() && selectedTags?.length === 0) {
      setSaveError('Please add a tag or write your meditation before saving.');
      return;
    }

    setIsSaving(true);
    setSaveError(null);

    try {
      const verseRef =
        verse?.reference ||
        `${book?.charAt(0)?.toUpperCase() + book?.slice(1)} ${chapter}:${verse?.number}`;

      // Try to find existing verse in user library
      let verseId = null;
      const { data: existingVerse } = await supabase
        ?.from('verses')
        ?.select('id')
        ?.eq('user_id', user?.id)
        ?.eq('reference', verseRef)
        ?.maybeSingle();

      if (existingVerse) {
        verseId = existingVerse?.id;
      } else {
        // Create verse entry
        const refMatch = verseRef?.match(/^([\w\s]+)\s+(\d+):(\d+)(?:-(\d+))?$/);
        if (refMatch) {
          const [, bookName, chapterNum, startVerse, endVerse] = refMatch;
          const { data: newVerse, error: verseErr } = await supabase
            ?.from('verses')
            ?.insert({
              user_id: user?.id,
              reference: verseRef,
              text: stripStrongs(verse?.text),
              book: bookName,
              chapter: parseInt(chapterNum),
              verse_start: parseInt(startVerse),
              verse_end: endVerse ? parseInt(endVerse) : null,
              bible_version: version || 'kjv',
              current_phase: 1,
              current_interval: 1.0,
              next_review_date: new Date()?.toISOString()?.split('T')?.[0],
            })
            ?.select('id')
            ?.single();
          if (!verseErr && newVerse) verseId = newVerse?.id;
        }
      }

      const reflectionData = {
        user_id: user?.id,
        verse_id: verseId,
        is_private: isPrivate,
        tags: selectedTags,
        meditation_text: meditationText?.trim() || null,
        book: book,
        chapter: parseInt(chapter),
        verse_number: verse?.number,
        verse_reference: verseRef,
        verse_text: stripStrongs(verse?.text),
      };

      const { error } = await reflectionService?.createReflection(reflectionData);
      if (error) throw error;

      setSaveSuccess(true);
      setTimeout(() => {
        onSaved?.();
        onClose?.();
      }, 800);
    } catch (err) {
      setSaveError(err?.message || 'Failed to save. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const verseText = stripStrongs(verse?.text);

  return (
    <div className="mt-3 rounded-xl border border-border bg-background/95 shadow-sm overflow-hidden">
      {/* Header bar */}
      <div className="flex items-center justify-between px-3 py-2 bg-muted/30 border-b border-border">
        <div className="flex items-center gap-1.5">
          <Icon name="Feather" size={13} className="text-primary" />
          <span className="text-xs font-semibold text-foreground tracking-wide uppercase">
            Meditate
          </span>
        </div>
        <button
          onClick={handleCancel}
          className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Close reflection"
        >
          <Icon name="X" size={14} />
        </button>
      </div>
      <div className="p-3 space-y-3">
        {/* Verse snippet */}
        <p className="text-xs text-muted-foreground italic leading-relaxed line-clamp-2">
          &ldquo;{verseText}&rdquo;
        </p>

        {/* Privacy toggle */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsPrivate(true)}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${
              isPrivate
                ? 'bg-primary text-primary-foreground border-primary'
                : 'bg-transparent text-muted-foreground border-border hover:border-primary/40'
            }`}
          >
            <Icon name="Lock" size={11} />
            Private
          </button>
          <button
            onClick={() => setIsPrivate(false)}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${
              !isPrivate
                ? 'bg-primary text-primary-foreground border-primary'
                : 'bg-transparent text-muted-foreground border-border hover:border-primary/40'
            }`}
          >
            <Icon name="Globe" size={11} />
            Public
          </button>
        </div>

        {/* Tags */}
        <div>
          <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">
            Tags
          </p>
          <div className="flex flex-wrap gap-1.5">
            {PRESET_TAGS?.map((tag) => {
              const active = selectedTags?.includes(tag);
              return (
                <button
                  key={tag}
                  onClick={() => toggleTag(tag)}
                  className={`px-2 py-0.5 rounded-full text-xs border transition-all ${
                    active
                      ? 'bg-primary/10 text-primary border-primary/40 font-medium' :'bg-transparent text-muted-foreground border-border hover:border-primary/30 hover:text-foreground'
                  }`}
                >
                  {active && <span className="mr-1">✓</span>}
                  {tag}
                </button>
              );
            })}

            {/* Custom tags already added */}
            {selectedTags?.filter((t) => !PRESET_TAGS?.includes(t))?.map((tag) => (
                <button
                  key={tag}
                  onClick={() => toggleTag(tag)}
                  className="px-2 py-0.5 rounded-full text-xs border bg-primary/10 text-primary border-primary/40 font-medium transition-all"
                >
                  ✓ {tag}
                  <span className="ml-1 opacity-60">×</span>
                </button>
              ))}

            {/* Custom tag button / input */}
            {showCustomInput ? (
              <div className="flex items-center gap-1">
                <input
                  ref={customInputRef}
                  autoFocus
                  value={customTagInput}
                  onChange={(e) => setCustomTagInput(e?.target?.value)}
                  onKeyDown={handleCustomKeyDown}
                  placeholder="Tag name…"
                  className="px-2 py-0.5 rounded-full text-xs border border-primary/40 bg-background text-foreground outline-none w-28 focus:ring-1 focus:ring-primary/30"
                />
                <button
                  onClick={addCustomTag}
                  className="p-0.5 rounded-full bg-primary text-primary-foreground"
                >
                  <Icon name="Check" size={10} />
                </button>
                <button
                  onClick={() => { setShowCustomInput(false); setCustomTagInput(''); }}
                  className="p-0.5 rounded-full text-muted-foreground hover:text-foreground"
                >
                  <Icon name="X" size={10} />
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowCustomInput(true)}
                className="px-2 py-0.5 rounded-full text-xs border border-dashed border-border text-muted-foreground hover:border-primary/40 hover:text-primary transition-all"
              >
                + Custom
              </button>
            )}
          </div>
        </div>

        {/* Meditation textarea */}
        <div className="relative">
          <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">
            Meditation
          </p>
          <div className="relative">
            <textarea
              ref={textareaRef}
              value={meditationText}
              onChange={(e) => setMeditationText(e?.target?.value)}
              placeholder="Write your meditation on this verse…"
              rows={3}
              className={`w-full text-sm text-foreground bg-muted/20 border rounded-lg px-3 py-2 pr-10 resize-none outline-none focus:ring-1 focus:ring-primary/30 focus:border-primary/40 placeholder:text-muted-foreground/50 transition-all ${
                isRecording ? 'border-red-400 ring-1 ring-red-300' : 'border-border'
              }`}
            />
            {/* Dictation button */}
            {hasSpeechSupport && (
              <button
                onClick={toggleDictation}
                title={isRecording ? 'Stop dictation' : 'Start dictation'}
                className={`absolute right-2 bottom-2 p-1.5 rounded-full transition-all ${
                  isRecording
                    ? 'bg-red-500 text-white animate-pulse' :'bg-muted text-muted-foreground hover:bg-primary/10 hover:text-primary'
                }`}
              >
                <Icon name={isRecording ? 'MicOff' : 'Mic'} size={13} />
              </button>
            )}
          </div>
          {isRecording && (
            <p className="text-[10px] text-red-500 mt-1 flex items-center gap-1">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
              Listening… speak your meditation
            </p>
          )}
        </div>

        {/* Error / Success */}
        {saveError && (
          <p className="text-xs text-destructive bg-destructive/10 px-2 py-1.5 rounded-lg">
            {saveError}
          </p>
        )}
        {saveSuccess && (
          <p className="text-xs text-green-600 bg-green-50 px-2 py-1.5 rounded-lg flex items-center gap-1">
            <Icon name="CheckCircle" size={12} />
            Meditation saved!
          </p>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between pt-1">
          <button
            onClick={handleClearAll}
            className="text-xs text-muted-foreground hover:text-destructive transition-colors"
          >
            Clear All
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={handleCancel}
              className="px-3 py-1 text-xs rounded-lg border border-border text-muted-foreground hover:text-foreground hover:border-foreground/30 transition-all"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving || saveSuccess}
              className="px-3 py-1 text-xs rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 disabled:opacity-50 transition-all flex items-center gap-1"
            >
              {isSaving ? (
                <><Icon name="Loader" size={11} className="animate-spin" /> Saving…</>
              ) : saveSuccess ? (
                <><Icon name="CheckCircle" size={11} /> Saved!</>
              ) : (
                'Save'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerseReflectionAccordion;
